import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../services/api";

/**
 * Org Administrator self-service for company OIDC (Okta) SSO.
 */
const CompanySsoSettings = ({ hideHeader = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [meta, setMeta] = useState({
    callback_url: "",
    frontend_url: "",
    callback_is_local: false,
    frontend_is_local: false,
    local_callback_url: null,
    local_frontend_url: null,
    env_dogfood_active: false,
  });
  const [form, setForm] = useState({
    provider: "okta",
    issuer: "",
    client_id: "",
    client_secret: "",
    allowed_email_domains: "",
    enabled: true,
    scopes: "openid profile email",
    client_secret_set: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/admin/oidc-config");
      if (!res.ok) throw new Error("Failed to load SSO settings");
      const data = await res.json();
      setMeta({
        callback_url: data.callback_url || "",
        frontend_url: data.frontend_url || "",
        callback_is_local: !!data.callback_is_local,
        frontend_is_local: !!data.frontend_is_local,
        local_callback_url: data.local_callback_url || null,
        local_frontend_url: data.local_frontend_url || null,
        env_dogfood_active: !!data.env_dogfood_active,
      });
      const cfg = data.config || {};
      setForm((prev) => ({
        ...prev,
        provider: cfg.provider || "okta",
        issuer: cfg.issuer || "",
        client_id: cfg.client_id || "",
        client_secret: "",
        allowed_email_domains: cfg.allowed_email_domains || "",
        enabled: cfg.enabled !== false,
        scopes: cfg.scopes || "openid profile email",
        client_secret_set: !!cfg.client_secret_set,
      }));
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess("Copied to clipboard");
      setTimeout(() => setSuccess(""), 2000);
    } catch {
      setError("Could not copy — select the text manually");
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const body = {
        provider: form.provider || "okta",
        issuer: form.issuer.trim(),
        client_id: form.client_id.trim(),
        allowed_email_domains: form.allowed_email_domains.trim(),
        enabled: !!form.enabled,
        scopes: form.scopes.trim() || "openid profile email",
      };
      if (form.client_secret.trim()) {
        body.client_secret = form.client_secret.trim();
      } else if (!form.client_secret_set) {
        setError("Client secret is required for the first save");
        setSaving(false);
        return;
      }
      const res = await apiFetch("/api/admin/oidc-config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg || d).join("; ")
              : data.message || "Save failed";
        throw new Error(msg);
      }
      setSuccess("Company SSO settings saved");
      setForm((prev) => ({ ...prev, client_secret: "", client_secret_set: true }));
      await load();
    } catch (err) {
      setError(typeof err.message === "string" ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDisable = async () => {
    if (!window.confirm("Disable company SSO for this organisation?")) return;
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/admin/oidc-config", { method: "DELETE" });
      if (!res.ok) throw new Error("Disable failed");
      setSuccess("Company SSO disabled");
      await load();
    } catch (err) {
      setError(err.message || "Disable failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-sm text-slate-600">Loading company SSO…</div>;
  }

  return (
    <div className="p-4 space-y-4 max-w-2xl">
      {!hideHeader && (
        <h3 className="text-base font-semibold text-slate-900">Company SSO (Okta)</h3>
      )}

      <p className="text-sm text-slate-600">
        Connect your organisation&apos;s Okta tenant so colleagues can use{" "}
        <span className="font-medium">Sign in with company SSO</span>. Roles stay in
        DecisionPen (new SSO users join as Viewer). Create an OIDC Web app in Okta,
        paste the redirect URI below, then enter issuer, client ID, and secret here.
      </p>

      {meta.env_dogfood_active && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          DecisionPen dogfood Okta env vars are also active for this organisation. A
          saved config below takes precedence for domain routing when enabled.
        </p>
      )}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Register in Okta (production)
        </div>
        <p className="text-xs text-slate-600">
          Paste these into your Okta app for live DecisionPen. Local dogfood URLs are
          listed separately when this machine uses localhost.
        </p>
        {(meta.callback_is_local || meta.frontend_is_local) && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Production API URL is not configured yet (set{" "}
            <code className="font-mono">BACKEND_URL</code> or{" "}
            <code className="font-mono">OIDC_PUBLIC_CALLBACK_BASE_URL</code> to your
            Cloud Run origin). Do not register localhost in a customer Okta tenant.
          </p>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500">Sign-in redirect URI</label>
          <div className="flex gap-2 items-center">
            <code className="flex-1 text-xs break-all bg-white border border-slate-200 rounded px-2 py-1.5">
              {meta.callback_url || "—"}
            </code>
            <button
              type="button"
              className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-100"
              onClick={() => copyText(meta.callback_url)}
              disabled={!meta.callback_url || meta.callback_is_local}
            >
              Copy
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500">App return host (informational)</label>
          <code className="text-xs break-all bg-white border border-slate-200 rounded px-2 py-1.5">
            {meta.frontend_url || "—"}
          </code>
        </div>
        {(meta.local_callback_url || meta.local_frontend_url) && (
          <div className="pt-2 mt-1 border-t border-slate-200 space-y-1">
            <div className="text-xs font-medium text-slate-500">Local development only</div>
            {meta.local_callback_url && (
              <code className="block text-xs break-all text-slate-500">
                {meta.local_callback_url}
              </code>
            )}
            {meta.local_frontend_url && (
              <code className="block text-xs break-all text-slate-500">
                {meta.local_frontend_url}
              </code>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {success && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          {success}
        </div>
      )}

      <form onSubmit={onSave} className="space-y-3">
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700">Issuer URL</span>
          <input
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="https://your-org.okta.com/oauth2/default"
            value={form.issuer}
            onChange={(e) => setForm({ ...form, issuer: e.target.value })}
            required
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700">Client ID</span>
          <input
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            required
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700">
            Client secret{form.client_secret_set ? " (leave blank to keep)" : ""}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={form.client_secret}
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            placeholder={form.client_secret_set ? "••••••••" : ""}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium text-slate-700">
            Allowed email domains
          </span>
          <input
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="acme.com"
            value={form.allowed_email_domains}
            onChange={(e) =>
              setForm({ ...form, allowed_email_domains: e.target.value })
            }
            required
          />
          <span className="text-xs text-slate-500">
            Comma-separated. Each domain can only belong to one organisation.
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          Enabled
        </label>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onDisable}
            className="px-3 py-2 text-sm rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Disable SSO
          </button>
        </div>
      </form>

      <details className="text-sm text-slate-600">
        <summary className="cursor-pointer font-medium text-slate-800">
          Okta admin checklist
        </summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li>Applications → Create App Integration → OIDC → Web Application</li>
          <li>Grant type: Authorization Code (PKCE used by DecisionPen)</li>
          <li>Sign-in redirect URI: paste the value above</li>
          <li>Assign users; turn off Federation Broker Mode if Assignments apply</li>
          <li>
            Security → API → Authorization Servers → default → Access Policies: allow
            this client and scopes openid profile email
          </li>
          <li>Paste issuer, client ID, and secret into this form and Save</li>
        </ol>
      </details>
    </div>
  );
};

export default CompanySsoSettings;
